import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNutritionByUPC } from '../utils/nutritionixAPI';

function ApiTest() {
  const navigate = useNavigate();
  const [upc, setUpc] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  // Common test barcodes you can try
  const testBarcodes = [
    { name: 'Coca-Cola 12oz', upc: '049000042566' },
    { name: 'Nature Valley Granola Bar', upc: '016000275263' },
    { name: 'Cheerios', upc: '016000275270' },
    { name: 'Clif Bar Chocolate Chip', upc: '722252100412' },
    { name: 'KIND Bar Dark Chocolate', upc: '602652171998' }
  ];

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const handleTest = async (testUpc = upc) => {
    if (!testUpc) {
      alert('Please enter a UPC code');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setLogs([]); // Clear previous logs

    addLog('🧪 MANUAL API TEST STARTED', 'info');
    addLog(`Testing UPC: ${testUpc}`, 'info');
    console.log('🧪 MANUAL API TEST STARTED');
    console.log('Testing UPC:', testUpc);

    try {
      addLog('📤 Calling getNutritionByUPC...', 'info');
      const data = await getNutritionByUPC(testUpc);
      addLog('✅ TEST SUCCESS!', 'success');
      addLog(`Found: ${data.food_name}`, 'success');
      console.log('✅ TEST SUCCESS:', data);
      setResult(data);
    } catch (err) {
      addLog('❌ TEST FAILED', 'error');
      addLog(`Error: ${err.message}`, 'error');
      console.error('❌ TEST FAILED:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh',
      color: '#fff'
    }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        style={{
          backgroundColor: '#2a2a2a',
          color: '#4ECDC4',
          border: '2px solid #4ECDC4',
          padding: '10px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        ← Back to Home
      </button>

      <h1 style={{ color: '#4ECDC4', marginTop: '10px' }}>🧪 Nutritionix API Test</h1>
      <p style={{ color: '#888', marginBottom: '30px' }}>
        Use this page to test the Nutritionix API without scanning a barcode.
        Logs will appear below in real-time.
      </p>

      {/* Live Log Viewer */}
      {logs.length > 0 && (
        <div style={{
          backgroundColor: '#0a0a0a',
          border: '2px solid #4ECDC4',
          borderRadius: '12px',
          padding: '15px',
          marginBottom: '20px',
          maxHeight: '200px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '13px'
        }}>
          <div style={{
            color: '#4ECDC4',
            fontWeight: 'bold',
            marginBottom: '10px',
            position: 'sticky',
            top: 0,
            backgroundColor: '#0a0a0a',
            paddingBottom: '5px'
          }}>
            📋 Live Logs:
          </div>
          {logs.map((log, index) => (
            <div key={index} style={{
              marginBottom: '5px',
              color: log.type === 'error' ? '#ff4444' :
                     log.type === 'success' ? '#4ECDC4' : '#aaa'
            }}>
              <span style={{ color: '#666' }}>[{log.timestamp}]</span> {log.message}
            </div>
          ))}
        </div>
      )}

      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#4ECDC4', marginTop: 0 }}>Quick Test Barcodes</h3>
        <p style={{ color: '#aaa', fontSize: '14px' }}>
          Click to test with common products:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {testBarcodes.map((item) => (
            <button
              key={item.upc}
              onClick={() => handleTest(item.upc)}
              disabled={loading}
              style={{
                backgroundColor: '#2a2a2a',
                color: '#fff',
                border: '2px solid #4ECDC4',
                padding: '12px',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                textAlign: 'left',
                opacity: loading ? 0.5 : 1
              }}
            >
              <strong>{item.name}</strong>
              <br />
              <span style={{ color: '#888', fontSize: '12px' }}>UPC: {item.upc}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#4ECDC4', marginTop: 0 }}>Custom UPC Test</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Enter UPC barcode (e.g., 049000042566)"
            value={upc}
            onChange={(e) => setUpc(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleTest();
              }
            }}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '2px solid #4ECDC4',
              backgroundColor: '#2a2a2a',
              color: '#fff',
              fontSize: '16px'
            }}
          />
          <button
            onClick={() => handleTest()}
            disabled={loading || !upc}
            style={{
              backgroundColor: '#4ECDC4',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading || !upc ? 'not-allowed' : 'pointer',
              opacity: loading || !upc ? 0.5 : 1
            }}
          >
            {loading ? '⏳ Testing...' : '🧪 Test'}
          </button>
        </div>
        <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
          💡 Tip: Open Developer Console (F12) to see detailed API logs
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#2a1a1a',
          border: '2px solid #ff4444',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#ff4444', marginTop: 0 }}>❌ Error</h3>
          <p style={{ color: '#fff', margin: 0 }}>{error}</p>
          <p style={{ color: '#888', fontSize: '14px', marginTop: '10px', marginBottom: 0 }}>
            Check the console for more details
          </p>
        </div>
      )}

      {result && (
        <div style={{
          backgroundColor: '#1a2a1a',
          border: '2px solid #4ECDC4',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <h3 style={{ color: '#4ECDC4', marginTop: 0 }}>✅ Success!</h3>

          <div style={{ marginBottom: '20px' }}>
            {result.photo?.thumb && (
              <img
                src={result.photo.thumb}
                alt={result.food_name}
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  marginBottom: '15px'
                }}
              />
            )}

            <h2 style={{ margin: '0 0 5px 0', color: '#fff' }}>
              {result.food_name}
            </h2>
            {result.brand_name && (
              <p style={{ margin: '0 0 15px 0', color: '#4ECDC4', fontSize: '16px' }}>
                {result.brand_name}
              </p>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginBottom: '15px'
            }}>
              <div style={{
                backgroundColor: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#888' }}>Serving</div>
                <div style={{ fontSize: '16px', color: '#fff', fontWeight: 600 }}>
                  {result.serving_qty} {result.serving_unit}
                </div>
              </div>

              <div style={{
                backgroundColor: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#888' }}>Calories</div>
                <div style={{ fontSize: '20px', color: '#FF6B35', fontWeight: 600 }}>
                  {Math.round(result.nf_calories)}
                </div>
              </div>

              <div style={{
                backgroundColor: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#888' }}>Protein</div>
                <div style={{ fontSize: '20px', color: '#4ECDC4', fontWeight: 600 }}>
                  {Math.round(result.nf_protein)}g
                </div>
              </div>

              <div style={{
                backgroundColor: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#888' }}>Carbs</div>
                <div style={{ fontSize: '16px', color: '#fff', fontWeight: 600 }}>
                  {Math.round(result.nf_total_carbohydrate || 0)}g
                </div>
              </div>

              <div style={{
                backgroundColor: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#888' }}>Fat</div>
                <div style={{ fontSize: '16px', color: '#fff', fontWeight: 600 }}>
                  {Math.round(result.nf_total_fat || 0)}g
                </div>
              </div>
            </div>
          </div>

          <details style={{ color: '#888' }}>
            <summary style={{ cursor: 'pointer', marginBottom: '10px' }}>
              📋 View Full API Response
            </summary>
            <pre style={{
              backgroundColor: '#0a0a0a',
              padding: '15px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              color: '#4ECDC4'
            }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        border: '2px solid #2a2a2a'
      }}>
        <h3 style={{ color: '#4ECDC4', marginTop: 0 }}>🔍 Debugging Checklist</h3>
        <ol style={{ color: '#aaa', lineHeight: '1.8' }}>
          <li>Open Developer Console (F12) and go to Console tab</li>
          <li>Click one of the test barcodes above</li>
          <li>Watch for console logs showing:
            <ul>
              <li>📤 Request being sent to proxy</li>
              <li>🌐 Proxy calling Nutritionix API</li>
              <li>📥 Response received</li>
            </ul>
          </li>
          <li>If you see errors, note the exact error message</li>
          <li>Check Netlify function logs for server-side errors</li>
        </ol>
      </div>
    </div>
  );
}

export default ApiTest;
